/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の共有未配置トレイ（全チーム共通の全体プール）の永続化。チーム白板とは別の Firestore ドキュメント (`shared-tray`) を読み書きし、チームタブを切り替えても未配置ブロックが入れ替わらないようにする。チーム白板の文書に未配置のまま残っていたブロック（共有トレイ導入前の保存・担当者が名簿から消えた予定）は引き取ってトレイへ合流させ、トレイ文書へ収まった時点で白板側へ通知して白板文書から落とさせる。編集履歴はチーム白板側に残るためこのフックは操作を積まず、「保存済みの内容と変わったか」を署名比較で検知してデバウンス保存する。リビジョンによる楽観ロック（409 なら取り直し）、上限付き再試行、アンマウント時の書き出しを担う
 * @graph-connects usePjSandboxBoard [calls] 共有トレイ文書の取得
 * @graph-connects useSavePjSandboxBoard [calls] 共有トレイ文書の全置換保存
 * @graph-connects fromStoredTrayBlocks [calls] 保存モデルから実行時モデルへの解決
 * @graph-connects toStoredTrayBlocks [calls] 実行時モデルから保存モデルへの変換
 * @graph-connects mergeAdoptedTrayBlocks [calls] チーム白板から引き取ったブロックの合流
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  PJ_SANDBOX_SHARED_TRAY_KEY,
  type PjSandboxStoredBlock,
} from '@cortex/domain-product-innovation-lab-types';
import { usePjSandboxBoard, useSavePjSandboxBoard } from '../../../../hooks/usePjSandbox.js';
import { ApiError } from '../../../../lib/api.js';
import { fromStoredTrayBlocks, mergeAdoptedTrayBlocks, toStoredTrayBlocks } from './sharedTray.js';
import { AUTOSAVE_DELAY_MS, MAX_SAVE_RETRIES, type SaveNotice } from './usePjSandboxPersistence.js';
import type { SandboxBlock } from './types.js';

interface UseSharedTrayPersistenceInput {
  /**
   * チーム白板の文書に未配置のまま入っていたため、共有トレイが引き取るブロック。
   * 白板を読み込むたびに渡され、ID 重複を除いてトレイへ合流させる。
   */
  adopted: readonly SandboxBlock[];
  /**
   * 引き取ったブロックが共有トレイ文書へ収まったときに呼ぶ。白板側はこれを受けてから
   * 自分の文書から落とす（先に落とすとトレイ側の保存が通らなかったときに案件が消える）。
   */
  onAdoptionPersisted: () => void;
}

interface UseSharedTrayPersistenceResult {
  /** 共有トレイに滞留している未配置ブロック（全チーム共通）。 */
  blocks: SandboxBlock[];
  setBlocks: Dispatch<SetStateAction<SandboxBlock[]>>;
  /** 閲覧者が共有トレイを編集できるか（サーバー側の判定をそのまま使う）。 */
  canEdit: boolean;
  /** 共有トレイの初回読み込み中か。 */
  loading: boolean;
  /** 現在の保存状態 / 通知。 */
  saveNotice: SaveNotice;
}

/**
 * 共有未配置トレイの永続化を丸ごと引き受けるフック。
 *
 * チーム白板 (`usePjSandboxPersistence`) が「保存待ちの操作 (`pendingOps`) があるときだけ
 * 保存する」のに対し、こちらは操作を持たない（履歴は表示中のチーム白板へ記録される）。
 * 代わりに **保存モデルの署名**を保持し、それと変わったときだけ書きに行くことで、
 * 読み込み直後の再保存や、内容が変わらない操作での無駄な PUT を避ける。
 * @graph-connects none
 */
export function useSharedTrayPersistence(
  input: UseSharedTrayPersistenceInput,
): UseSharedTrayPersistenceResult {
  const { adopted, onAdoptionPersisted } = input;

  const [blocks, setBlocks] = useState<SandboxBlock[]>([]);
  const [saveNotice, setSaveNotice] = useState<SaveNotice>({ kind: 'idle' });
  /** 再試行のトリガー。保存が失敗して積み直すとき進めて、保存 effect を張り直す。 */
  const [saveAttempt, setSaveAttempt] = useState(0);

  const revisionRef = useRef(0);
  /**
   * 反映済みのサーバー状態を表す署名（リビジョン）。同じ署名で二重にハイドレートしない
   * = 自分の保存結果でローカル編集を巻き戻さないための番人。
   */
  const hydratedRevisionRef = useRef<number | null>(null);
  /**
   * サーバーに入っている（はずの）内容の署名。`null` は **まだ 1 度も読み込んでいない** ことを
   * 表し、その間は保存しない。空配列で上書きして他の人のトレイを消さないための門番。
   */
  const savedSignatureRef = useRef<string | null>(null);
  const saveFailureCountRef = useRef(0);
  /** サーバーに入っていると確認できている保存モデル（引き取り完了の判定に使う）。 */
  const serverBlocksRef = useRef<PjSandboxStoredBlock[]>([]);

  // 引き取りの入力は effect / 保存コールバックから最新値を読むため ref に写す
  // （値が変わるたびに effect や保存関数を張り直さない）。
  const adoptedRef = useRef(adopted);
  adoptedRef.current = adopted;
  const onAdoptionPersistedRef = useRef(onAdoptionPersisted);
  onAdoptionPersistedRef.current = onAdoptionPersisted;

  /**
   * 引き取ったブロックがすべてサーバーの共有トレイに入っていれば、白板側へ完了を伝える。
   * 1 件でも欠けていれば伝えない（白板文書が唯一の持ち主のまま残る）。
   */
  const confirmAdoptionIfStored = useCallback(() => {
    const pending = adoptedRef.current;
    if (pending.length === 0) return;
    const storedIds = new Set(serverBlocksRef.current.map((block) => block.id));
    if (pending.every((block) => storedIds.has(block.id))) onAdoptionPersistedRef.current();
  }, []);

  const boardQuery = usePjSandboxBoard(PJ_SANDBOX_SHARED_TRAY_KEY);
  const saveBoard = useSavePjSandboxBoard();
  const canEdit = boardQuery.data?.canEdit ?? false;

  const stored = useMemo(() => toStoredTrayBlocks(blocks), [blocks]);
  const signature = useMemo(() => JSON.stringify(stored), [stored]);

  // 保存を実行する瞬間の最新値を読むための ref（保存関数自体は安定参照にする）。
  const saveStateRef = useRef({ stored, signature });
  saveStateRef.current = { stored, signature };

  /** 保存済みの署名と食い違っていれば、共有トレイを全置換で書き込む。 */
  const flushSave = useCallback(() => {
    const s = saveStateRef.current;
    const previousSignature = savedSignatureRef.current;
    if (previousSignature === null || previousSignature === s.signature) return;

    // 応答を待つ間に同じ内容を二重送信しないよう、先に「保存したはず」の署名へ進める。
    // 失敗したら元へ戻して再試行できるようにする。
    savedSignatureRef.current = s.signature;
    setSaveNotice({ kind: 'saving' });

    saveBoard.mutate(
      {
        boardKey: PJ_SANDBOX_SHARED_TRAY_KEY,
        body: {
          baseRevision: revisionRef.current,
          blocks: s.stored,
          candidates: [],
          // 編集履歴は表示中のチーム白板側に記録するため、ここでは操作を送らない。
          operations: [],
        },
      },
      {
        onSuccess: (data) => {
          saveFailureCountRef.current = 0;
          revisionRef.current = data.board.revision;
          serverBlocksRef.current = s.stored;
          confirmAdoptionIfStored();
          // 自分の保存結果は既にローカル state に反映済み。先に反映済みとして記録しておき、
          // ハイドレート effect が走ってもローカルの続きの編集を巻き戻さないようにする。
          hydratedRevisionRef.current = data.board.revision;
          setSaveNotice({
            kind: 'saved',
            at: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
          });
        },
        onError: (error) => {
          const status = error instanceof ApiError ? error.status : 0;
          if (status === 403) {
            setSaveNotice({ kind: 'forbidden' });
            return;
          }
          if (status === 409) {
            // 他の編集者に追い越された。ローカルの続きは破棄し、サーバーの最新を取り直す。
            const updatedByName =
              error instanceof ApiError ? error.details?.updatedByName : undefined;
            setSaveNotice({
              kind: 'conflict',
              actor: typeof updatedByName === 'string' ? updatedByName : '',
            });
            hydratedRevisionRef.current = null;
            void boardQuery.refetch();
            return;
          }

          saveFailureCountRef.current += 1;
          setSaveNotice({ kind: 'failed' });
          // 一時的な失敗は編集内容を失わないよう、保存済み署名を戻して再試行させる。
          // ただし連続失敗が続く場合は打ち切り、叩き続けない。
          if (saveFailureCountRef.current <= MAX_SAVE_RETRIES) {
            savedSignatureRef.current = previousSignature;
            setSaveAttempt((prev) => prev + 1);
          }
        },
      },
    );
  }, [saveBoard, boardQuery, confirmAdoptionIfStored]);

  // サーバーの共有トレイを実行時モデルへ解決して流し込む。
  useEffect(() => {
    const data = boardQuery.data;
    if (data === undefined) return;
    if (hydratedRevisionRef.current === data.board.revision) return;
    hydratedRevisionRef.current = data.board.revision;

    // サーバーの内容に、白板から引き取ったブロックを重ねて流し込む。重ねずに置き換えると、
    // 白板の読み込みが先に済んでいたときに引き取ったぶんを取りこぼす。
    const resolved = fromStoredTrayBlocks(data.board.blocks);
    setBlocks(mergeAdoptedTrayBlocks(resolved, adoptedRef.current));
    revisionRef.current = data.board.revision;
    // 署名は **サーバーの内容** で持つ。引き取ったぶんは差分として残り、自動保存が拾う。
    savedSignatureRef.current = JSON.stringify(toStoredTrayBlocks(resolved));
    serverBlocksRef.current = data.board.blocks;
    confirmAdoptionIfStored();
  }, [boardQuery.data, confirmAdoptionIfStored]);

  // 白板の読み込みが後から済んだときの引き取り。既にトレイに居るものは足さない。
  useEffect(() => {
    if (adopted.length === 0) return;
    setBlocks((prev) => mergeAdoptedTrayBlocks(prev, adopted));
    // 既にサーバーへ入っている（別の閲覧者が先に引き取った）なら、保存を待たずに完了を伝える。
    confirmAdoptionIfStored();
  }, [adopted, confirmAdoptionIfStored]);

  // 自動保存: 内容が変わってから AUTOSAVE_DELAY_MS 静かになったら 1 回だけ書きに行く。
  useEffect(() => {
    if (!canEdit) return;
    if (savedSignatureRef.current === null || savedSignatureRef.current === signature) return;
    const timer = setTimeout(flushSave, AUTOSAVE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [signature, saveAttempt, canEdit, flushSave]);

  // 画面を離れるときに保存待ちを取りこぼさないよう、アンマウント時にも書き出す。
  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;
  useEffect(() => {
    return () => {
      flushSaveRef.current();
    };
  }, []);

  return { blocks, setBlocks, canEdit, loading: boardQuery.isLoading, saveNotice };
}
